import {
  calculateIndividualCompanyCost,
  calculateIndividualTotalCost,
  findDependentCostforSelectedPlan,
  calculateCompanyDependentCost,
  calculateIndividualDependentCompanyCost,
  calculateIndividualDependentTotalCost,
} from "EmployerApplication/Components/ComprehensiveCostEstimation/CostCalculations";

import { TIERED_CONTRIBUTIONS_SORTING_ORDER, CONTRIBUTION_TYPES } from "Common/Constants";

// 1. Use a builder function to specify exactly what you want using passed in props
//    and get the rest of the needed information from the store
// 2. Returns the the company totalts plus the employee breakdowns
// structure as follows
// {
//   companyTotals: {
//     totalPremium: 0,
//     totalCompanyCost: 0,
//     totalEmployeeCost: 0,
//   },
//   employeesBreakdowns: [
//     {
//       name: "John Doe",
//       premium: 0,
//       companyCost: 0,
//       employeeCost: 0,
//       dependentsPremium: 0,
//       dependentsCompanyCost: 0,
//       dependentsEmployeeCost: 0,
//       individualDependentCosts: [
//         {
//           name: "Spouse",
//           premium: 0,
//           companyCost: 0,
//           employeeCost: 0,
//         },
//         {
//           name: "Child",
//           premium: 0,
//           companyCost: 0,
//           employeeCost: 0,
//         },
//       ],
//     },
//   ],
// }

const quoteformattedEmployeeProfiles = (employeeProfiles) =>
  employeeProfiles.map((employee) => {
    const employeeCopy = {
      ...employee,
      is_enrolling: true,
      is_enrolling_dental: true,
      is_enrolling_vision: true,
      spouse_enrollment: employee.spouse_age
        ? {
            is_enrolling: true,
            is_enrolling_dental: true,
            is_enrolling_vision: true,
          }
        : undefined,
      children_enrollment: employee.children?.map((_age) => ({
        is_enrolling: true,
        is_enrolling_dental: true,
        is_enrolling_vision: true,
      })),
    };

    const dependents = employeeCopy.children.map((childAge, index) => {
      const childEnrollment = employeeCopy.children_enrollment[index];
      return {
        name: "Child",
        age: childAge,
        ...childEnrollment,
      };
    });

    if (employeeCopy.spouse_age) {
      const spouse = {
        name: "Spouse",
        age: employeeCopy.spouse_age,
        ...employeeCopy.spouse_enrollment,
      };

      dependents.unshift(spouse);
    }
    return {
      ...employeeCopy,
      dependents,
    };
  });

const getDefaultContribution = (contributions) => {
  let defaultContribution;
  for (const role of TIERED_CONTRIBUTIONS_SORTING_ORDER) {
    defaultContribution = contributions.find((contribution) => contribution.role === role);

    if (defaultContribution !== undefined) {
      return defaultContribution;
    }
  }
};

// Helper function to get contribution for an employee
const getContributionForEmployee = (contributions, employeeProfile) => {
  if (contributions.length === 0) {
    return {
      employeePercentage: 50,
      dependentPercentage: 0,
      employeeFixedAmount: null,
      dependentFixedAmount: null,
    };
  }
  if (contributions.length === 1) {
    return contributions[0];
  }
  const contribution = contributions.find((contribution) => contribution.role === employeeProfile.role);

  return contribution || getDefaultContribution(contributions);
};

const computeDependentCost = (
  dependentName,
  age,
  enrollment,
  employeeProfile,
  selectedPlan,
  contributionType,
  contribution,
  dentalPlan,
  visionPlan,
  medicalPlans,
  employeeIndex,
  dependentIndex,
) => {
  const premium = findDependentCostforSelectedPlan(
    dependentName,
    age,
    employeeProfile,
    selectedPlan,
    dentalPlan,
    visionPlan,
    enrollment,
    employeeIndex,
    dependentIndex,
  );

  const companyCost = calculateCompanyDependentCost(
    contributionType,
    contribution,
    dependentName,
    age,
    employeeProfile,
    selectedPlan,
    dentalPlan,
    visionPlan,
    enrollment,
    employeeIndex,
    dependentIndex,
    medicalPlans,
  );

  const dependentCost = {
    name: dependentName,
    premium,
    companyCost,
    employeeCost: premium - companyCost,
  };

  return dependentCost;
};

const calculateDependentCosts = (employeeIndex, selectedPlan, contributionType, contribution, medicalPlans, dentalPlan, visionPlan, employeeProfile) => {
  const dependentCosts = [];
  // Calculate spouse
  if (employeeProfile.spouse_age) {
    dependentCosts.push(
      computeDependentCost(
        "Spouse",
        employeeProfile.spouse_age,
        employeeProfile.spouse_enrollment,
        employeeProfile,
        selectedPlan,
        contributionType,
        contribution,
        dentalPlan,
        visionPlan,
        medicalPlans,
        employeeIndex,
        0,
      ),
    );
  }
  // Calculate children
  if (employeeProfile.children) {
    employeeProfile.children.forEach((child, childIndex) => {
      const dependentIndex = employeeProfile.spouse_age ? childIndex + 1 : childIndex;
      dependentCosts.push(
        computeDependentCost(
          `Child ${childIndex + 1}`,
          child,
          employeeProfile.children_enrollment[childIndex],
          employeeProfile,
          selectedPlan,
          contributionType,
          contribution,
          dentalPlan,
          visionPlan,
          medicalPlans,
          employeeIndex,
          dependentIndex,
        ),
      );
    });
  }
  return dependentCosts;
};

const combineDependentCosts = (dependentCosts) => {
  const dependentsPremium = dependentCosts.reduce((sum, { premium }) => sum + premium, 0);

  const dependentsCompanyCost = dependentCosts.reduce((sum, { companyCost }) => sum + companyCost, 0);

  const dependentsEmployeeCost = dependentCosts.reduce((sum, { employeeCost }) => sum + employeeCost, 0);

  return {
    dependentsPremium,
    dependentsCompanyCost,
    dependentsEmployeeCost,
  };
};

const combineEmployeeCosts = (employeeCosts) => {
  const employeesPremium = employeeCosts.reduce((sum, { premium }) => sum + premium, 0);

  const employeesCompanyCost = employeeCosts.reduce((sum, { companyCost }) => sum + companyCost, 0);

  const dependentsPremium = employeeCosts.reduce((sum, { dependentsPremium }) => sum + (dependentsPremium || 0), 0);

  const dependentsCompanyCost = employeeCosts.reduce((sum, { dependentsCompanyCost }) => sum + (dependentsCompanyCost || 0), 0);

  const premium = employeesPremium + dependentsPremium;
  const companyCost = employeesCompanyCost + dependentsCompanyCost;
  const employeeCost = premium - companyCost;
  return {
    premium,
    companyCost,
    employeeCost,
  };
};

// prop types:
// propsEmployeeProfile (emplyoeeProfile object),
// blankEmployeeProfile (boolean),
// propsContributions (array of contribution objects),
// propsContributionType (string),
// medicalPlans
// dentalPlan
// visionPlan
export const costCalculationBuilder = (props) => {
  const { contributions, contributionType, medicalPlans, dentalPlan, visionPlan, lifePlan, fullTimeEmployees, shortTermEmployeeCosts, longTermEmployeeCosts } =
    props;
  const employeeProfiles = props.overwriteEmployeeProfilesEnrollingStatus ? quoteformattedEmployeeProfiles(props.employeeProfiles) : props.employeeProfiles;

  const fullTimeEmployeeCount = fullTimeEmployees ? fullTimeEmployees.filter((ee) => ee).length : employeeProfiles.filter((ee) => ee.full_time).length;

  const employeeLifeCost = lifePlan && lifePlan.total_cost && fullTimeEmployeeCount > 0 ? lifePlan.total_cost / fullTimeEmployeeCount : 0;

  const employeesCosts = employeeProfiles.map((employeeProfile, index) => {
    const selectedPlan = medicalPlans.find((plan) => plan.id === employeeProfile.selected_medical_plan) || medicalPlans[0];
    const contribution = getContributionForEmployee(contributions, employeeProfile);

    const isFullTimeEmployee = fullTimeEmployees ? fullTimeEmployees[index] : employeeProfile.full_time;
    const employeeLifeCostForIndividual = isFullTimeEmployee && employeeProfile.is_enrolling_life ? employeeLifeCost : 0;
    const shortTermDisabilityCostForIndividual = isFullTimeEmployee && shortTermEmployeeCosts ? shortTermEmployeeCosts[index]?.composite_cost : 0;
    const longTermDisabilityCostForIndividual = isFullTimeEmployee && longTermEmployeeCosts ? longTermEmployeeCosts[index]?.composite_cost : 0;
    const disabilityCostForIndividual = shortTermDisabilityCostForIndividual + longTermDisabilityCostForIndividual;

    const premium = calculateIndividualTotalCost(
      index,
      selectedPlan,
      dentalPlan,
      visionPlan,
      employeeProfile,
      employeeLifeCostForIndividual,
      disabilityCostForIndividual,
    );

    const companyCost = calculateIndividualCompanyCost(
      contributionType,
      contribution,
      index,
      selectedPlan,
      medicalPlans,
      dentalPlan,
      visionPlan,
      employeeProfile,
      employeeLifeCostForIndividual,
      disabilityCostForIndividual,
    );

    const dependentCosts = calculateDependentCosts(index, selectedPlan, contributionType, contribution, medicalPlans, dentalPlan, visionPlan, employeeProfile);

    let dependentsPremium;
    let dependentsCompanyCost;
    let dependentsEmployeeCost;
    if (dependentCosts.length > 0) {
      ({ dependentsPremium, dependentsCompanyCost, dependentsEmployeeCost } = combineDependentCosts(dependentCosts));
    }

    if (contributionType === CONTRIBUTION_TYPES.fixed) {
      dependentsPremium = calculateIndividualDependentTotalCost(index, employeeProfile, selectedPlan, dentalPlan, visionPlan);

      dependentsCompanyCost = calculateIndividualDependentCompanyCost(contribution, employeeProfile, index, selectedPlan);

      dependentsEmployeeCost = dependentsPremium - dependentsCompanyCost;
    }

    return {
      name: employeeProfile.name,
      premium,
      companyCost,
      employeeCost: premium - companyCost,
      individualDependentCosts: dependentCosts,
      dependentsPremium,
      dependentsCompanyCost,
      dependentsEmployeeCost,
    };
  });

  const combindEmployeeCosts = combineEmployeeCosts(employeesCosts);

  return {
    companyTotals: {
      totalPremium: combindEmployeeCosts.premium,
      totalCompanyCost: combindEmployeeCosts.companyCost,
      totalEmployeeCost: combindEmployeeCosts.employeeCost,
    },

    employeesBreakdowns: employeesCosts,
  };
};

export default costCalculationBuilder;
